// import algosdk from "algosdk";
// import { setUseWalletState } from "./localStorage.service";
// import { algodClient } from "../App";
import { TelegramData, Theme } from "../interfaces";
import { setAppUrl, setReferralToken } from "./localStorage.service";

export enum WalletKeys {
  UseWallet = 'txnlab-use-wallet',
  Pera = 'PeraWallet.Wallet',
  Defly = 'DeflyWallet.Wallet',
};

export enum ParentEvents {
  SOLANA_TX_SENT = 'SOLANA_TX_SENT',
  SOLANA_TX_ERROR = 'SOLANA_TX_ERROR',
  SOLANA_CONNECTED = 'SOLANA_CONNECTED',
  SOLANA_CONNECTION_ERROR = 'SOLANA_CONNECTION_ERROR',
  SOLANA_DISCONNECTED = 'SOLANA_DISCONNECTED',
  SOLANA_DISCONNECT_ERROR = 'SOLANA_DISCONNECT_ERROR',
  SOLANA_MESSAGE_SIGNED = 'SOLANA_MESSAGE_SIGNED',
  SOLANA_MESSAGE_SIGNING_ERROR = 'SOLANA_MESSAGE_SIGNING_ERROR',
  INITIALIZED = 'INITIALIZED',
  CHECK_SOLANA_WALLET = 'CHECK_SOLANA_WALLET',
  CLEANED_URL = 'CLEAN_URL',
  THEME_SET = 'THEME_SET',
}

export enum ParentMessages {
  SOLANA_CONNECT = 'SOLANA_CONNECT',
  SOLANA_DISCONNECT = 'SOLANA_DISCONNECT',
  SOLANA_SIGN_MESSAGE = 'SOLANA_SIGN_MESSAGE',
  SOLANA_SEND_TRANSACTION = 'SOLANA_SEND_TRANSACTION',
  INIT = 'INIT',
  CHECK_SOLANA_WALLET = 'CHECK_SOLANA_WALLET',
  CLEAN_URL = 'CLEAN_URL',
  THEME_SET = 'THEME_SET',
}

export enum ParentAppStatus {
  INITIALIZED = 'INITIALIZED',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
};

export enum KycFinishStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
}

interface DepositTx {
  amount: number,
  loginAddress: Uint8Array,
  loginChainId: number,
  tmc: string,
  tokenAddress: string,
}

interface EmailConfirmation {
  email: string;
  hash: string;
}

export class ParentAppService {
  private static instance: ParentAppService;
  private parentAppStatus: ParentAppStatus;
  private kycStatus: Promise<KycFinishStatus> | KycFinishStatus = null;
  private initPromise: Promise<void> | null = null;
  private isInitComplete = false;
  private emailData: EmailConfirmation;
  private telegramData: TelegramData = null;
  private refferalClick: string;

  private constructor() {
    this.init();
  }

  public static getInstance(): ParentAppService {
    if (!ParentAppService.instance) {
      ParentAppService.instance = new ParentAppService();
    }
    return ParentAppService.instance;
  };

  private async init(): Promise<void> {
    if (this.isInitComplete) return;
    if (!this.initPromise) {
      this.initPromise = new Promise<void>((res, rej) => {
        const isAppEmbedded = this.checkIfAppEmbedded();
        if (!isAppEmbedded) {
          this.handleAndStoreAppUrl(window.location.toString(), isAppEmbedded);
          this.isInitComplete = true;
          res();
          return;
        }
  
        this.parentAppStatus = ParentAppStatus.PENDING;
        const handleParentMessage = (event: MessageEvent) => {
          const timeOutTimer = setTimeout(() => {
            this.parentAppStatus = ParentAppStatus.FAILED;
            this.removeListener(handleParentMessage);
            rej('Parent app connection timeout');
          }, 15000);
  
          if (event?.data?.key === ParentEvents.INITIALIZED) {
            this.log(event.data, false);
            const { appUrl, title, icon } = event?.data?.value;
            this.handleAndStoreAppUrl(appUrl, isAppEmbedded);
            if (title) {
              document.title = title;
            }
            if (icon) {
              const link = document.querySelector('link[rel="icon"]');
              if (link) {
                link.setAttribute('href', icon);
              }
            }
            this.parentAppStatus = ParentAppStatus.INITIALIZED;
            clearTimeout(timeOutTimer);
            this.isInitComplete = true;
            this.removeListener(handleParentMessage);
            res();
          }
        };
  
        this.postMessage(ParentMessages.INIT, handleParentMessage);
      });
    }
  
    return this.initPromise;
  }

  private parseErrorPayload(payload: unknown): Error & { code?: number; raw?: any } {
    let obj: any = payload;
    if (typeof payload === 'string') {
      try { obj = JSON.parse(payload); } catch { obj = { message: String(payload) }; }
    }
    const err = new Error(obj?.message || 'Unknown wallet error') as Error & { code?: number; raw?: any };
    if (obj?.code !== undefined) err.code = Number(obj.code);
    err.raw = obj;
    return err;
  }

  private handleAndStoreAppUrl(appUrl: string | undefined, isAppEmbedded: boolean) {
    if (!appUrl) return;

    try {
      const url = new URL(appUrl);
      const hashParams = new URLSearchParams(url.hash.substring(1));

      const token = url.searchParams.get("r");
      if (token) {
        setReferralToken(token);
        this.refferalClick = token;
      }

      const kycStatus = url.searchParams.get("kycStatus");
      if (Object.values(KycFinishStatus).includes(kycStatus as KycFinishStatus)) {
        this.kycStatus = kycStatus as KycFinishStatus;
      } else {
        this.kycStatus = null;
      }

      const email = url.searchParams.get("email");
      const hash = url.searchParams.get("hash");
      if (email && hash) {
        this.emailData = { email, hash };
      }

      const telegramQuery = hashParams.get('tgAuthResult');
      if (telegramQuery) {
        const user = JSON.parse(Buffer.from(telegramQuery, 'base64').toString()) as TelegramData;
        this.telegramData = user;
      }

      const cleanedUrl = isAppEmbedded ? url.origin + url.pathname : url.origin;
      setAppUrl(cleanedUrl);
    } catch (error) {
      console.error("Invalid URL provided to updateLocalStorage:", appUrl, error);
    }
  }

  public async getKycStatus(): Promise<KycFinishStatus | null> {
    await this.init();
    return this.kycStatus;
  }

  public async getEmailData(): Promise<EmailConfirmation | null> {
    await this.init();
    return this.emailData;
  }

  public async getTelegramData(): Promise<TelegramData | null> {
    await this.init();
    return this.telegramData;
  }

  public async getRefferalClick(): Promise<string | null> {
    await this.init();
    return this.refferalClick;
  }

  public checkIfAppEmbedded = () => window.location !== window.parent.location;

  private postMessage(key: ParentMessages, handler: (event: MessageEvent) => void, value?: string) {
    if (window.parent) {
      window.addEventListener('message', handler, false);
      if (document.referrer) {
        this.log({ key, value }, true);
        window.parent.postMessage({ key, value }, new URL(document.referrer).origin);
      }
    }
  }

  private removeListener(handler: (event: MessageEvent) => void) {
    window.removeEventListener('message', handler);
  }

  public cleanQuery() {
    if (this.checkIfAppEmbedded()) {
      return new Promise<boolean>((res, rej) => {
        const handleParentMessage = (event: MessageEvent) => {
          if (event?.data?.key === ParentEvents.CLEANED_URL) {
            this.log(event.data, false);
            this.removeListener(handleParentMessage);
            res(event.data.key);
          }
        };
        this.postMessage(ParentMessages.CLEAN_URL, handleParentMessage);
      });
    } else {
      window.history.replaceState({}, document.title, window.location.origin + window.location.pathname);
    }
  }

  public connectSolana(provider: 'PHANTOM' | 'SOLFLARE' | 'MOBILE' | 'BACKPACK', reconnect: boolean = false) {
    return new Promise<string>((res, rej) => {
      const handleParentMessage = (event: MessageEvent) => {
        if (event?.data?.key === ParentEvents.SOLANA_CONNECTED) {
          this.log(event.data, false);
          this.removeListener(handleParentMessage);
          res(event.data.value);
        }
        if (event?.data?.key === ParentEvents.SOLANA_CONNECTION_ERROR) {
          const err = this.parseErrorPayload(event.data.value);
          this.maybeHandleUnauthorized(err);
          this.log(event.data, false);
          this.removeListener(handleParentMessage);
          rej(err);
        }
      };

      this.postMessage(ParentMessages.SOLANA_CONNECT, handleParentMessage, JSON.stringify({ provider, silent: reconnect }));
    });
  };

  private async maybeHandleUnauthorized(err: { code?: number }) {
    if (Number(err?.code) === 4100) {
      try { await this.disconnectSolana(); } catch {}
    }
  }

  public disconnectSolana() {
    return new Promise<void>((res, rej) => {
      const handleParentMessage = (event: MessageEvent) => {
        if (event?.data?.key === ParentEvents.SOLANA_DISCONNECTED) {
          this.log(event.data, false);
          this.removeListener(handleParentMessage);
          res();
        }
        if (event?.data?.key === ParentEvents.SOLANA_DISCONNECT_ERROR) {
          const err = this.parseErrorPayload(event.data.value);
          this.maybeHandleUnauthorized(err);
          this.log(event.data, false);
          this.removeListener(handleParentMessage);
          res();
        }
      };

      this.postMessage(ParentMessages.SOLANA_DISCONNECT, handleParentMessage);
    });
  };

  public checkSolanaWallet() {
    if (this.checkIfAppEmbedded()) {
      return new Promise<{ phantom: boolean, solflare: boolean, backpack: boolean }>((res, rej) => {
        const handleParentMessage = (event: MessageEvent) => {
          if (event?.data?.key === ParentEvents.CHECK_SOLANA_WALLET) {
            const err = this.parseErrorPayload(event.data.value);
            this.maybeHandleUnauthorized(err);
            this.log(event.data, false);
            this.removeListener(handleParentMessage);
            res(JSON.parse(event.data.value));
          }
        };
        this.postMessage(ParentMessages.CHECK_SOLANA_WALLET, handleParentMessage);
      });
    }

    return Promise.resolve({ phantom: false, solflare: false, backpack: false });
  }

  public signMessageSolana(message: string, encoding?: BufferEncoding) {
    return new Promise<string>((res, rej) => {
      const handleParentMessage = (event: MessageEvent) => {
        if (event?.data?.key === ParentEvents.SOLANA_MESSAGE_SIGNED) {
          this.log(event.data, false);
          console.log(`Parent Solana message signed`);
          this.removeListener(handleParentMessage);
          res(event.data.value);
        }
        if (event?.data?.key === ParentEvents.SOLANA_MESSAGE_SIGNING_ERROR) {
          const err = this.parseErrorPayload(event.data.value);
          this.maybeHandleUnauthorized(err);
          this.log(event.data, false);
          this.removeListener(handleParentMessage);
          rej(err);
        }
      };

      this.postMessage(ParentMessages.SOLANA_SIGN_MESSAGE, handleParentMessage, JSON.stringify({message, encoding}));
    });
  };

  public sendTxSolana(tx: DepositTx) {
    return new Promise<string>((res, rej) => {
      const handleParentMessage = (event: MessageEvent) => {
        if (event?.data?.key === ParentEvents.SOLANA_TX_SENT) {
          this.log(event.data, false);
          this.removeListener(handleParentMessage);
          res(event.data.value);
        }
        if (event?.data?.key === ParentEvents.SOLANA_TX_ERROR) {
          const err = this.parseErrorPayload(event.data.value);
          this.maybeHandleUnauthorized(err);
          this.log(event.data, false);
          this.removeListener(handleParentMessage);
          rej(err);
        }
      };

      const serializedTx = JSON.stringify({
        ...tx,
        loginAddress: Array.from(tx.loginAddress),
      })

      this.postMessage(ParentMessages.SOLANA_SEND_TRANSACTION, handleParentMessage, serializedTx);
    });
  };

  private log(message: any, direction: boolean) {
    console.log(`PARENT APP FE: ${direction ? 'send' : 'received'}`, message);
  };

  public setTheme(theme: Theme) {
    return new Promise<boolean>((res, rej) => {
      const handleParentMessage = (event: MessageEvent) => {
        if (event?.data?.key === ParentEvents.THEME_SET) {
          this.log(event.data, false);
          res(event.data.value);
          this.removeListener(handleParentMessage);
        }
      };

      this.postMessage(ParentMessages.THEME_SET, handleParentMessage, JSON.stringify(theme));
    });
  }

}

export const checkIfShouldInheritWallet = () => {
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams?.get("walletInheritance") && searchParams?.get("walletInheritance") === "true") {
    return true;
  }
  return false;
}
